
/**
 * AI Chat Service - Response Generator
 * Generates AI responses focused on Bluetoothcash.net knowledge
 */

import { knowledgeBase, Language } from './knowledge';
import { findKeywordMatch, findContextualResponse, getRandomFallback, getUniqueResponse } from './keywordMatcher';

// Store recent responses to avoid repetition (up to 5 most recent)
const recentResponses: string[] = [];
const MAX_RECENT_RESPONSES = 5;

// Add response to recent list and maintain max size
const addToRecentResponses = (response: string) => {
  recentResponses.push(response);
  if (recentResponses.length > MAX_RECENT_RESPONSES) {
    recentResponses.shift(); // Remove oldest response
  }
};

/**
 * Generates an AI response based on user message and context
 * @param message User message
 * @param currentPath Current page path
 * @param language User preferred language
 * @returns AI response
 */
export const generateAIResponse = async (
  message: string,
  currentPath: string,
  language: string = 'en'
): Promise<string> => {
  // Simulate network delay for realistic AI response timing
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Ensure valid language or default to English
  const lang = (language as Language) || 'en';
  const knowledge = knowledgeBase[lang] || knowledgeBase.en;
  
  // Preprocess message to lowercase for better matching
  const lowerMessage = message.toLowerCase();
  
  // Track whether we've found a response
  let response: string | undefined;
  
  // Handle common greetings
  if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
    response = lang === 'es' 
      ? "¡Hola! Soy tu asistente de bluetooth$. ¿En qué puedo ayudarte hoy?" 
      : lang === 'fr'
      ? "Bonjour! Je suis votre assistant bluetooth$. Comment puis-je vous aider aujourd'hui?"
      : "Hello! I'm your bluetooth$ assistant. How can I help you today?";
  }
  
  // Handle card verification/payment related questions
  if (lowerMessage.includes('card') && (lowerMessage.includes('verify') || lowerMessage.includes('verification'))) {
    response = lang === 'es' 
      ? "Nuestro sistema de verificación de tarjetas combina IA y Stripe para máxima seguridad. Primero verificamos con nuestro algoritmo AI, y solo enviamos a Stripe cuando es necesario para procesar el pago."
      : lang === 'fr'
      ? "Notre système de vérification de carte combine l'IA et Stripe pour une sécurité maximale. Nous vérifions d'abord avec notre algorithme d'IA, et n'envoyons à Stripe que lorsque c'est nécessaire pour traiter le paiement."
      : "Our card verification system combines AI and Stripe for maximum security. We first verify with our AI algorithm, and only send to Stripe when necessary for payment processing.";
  }
  
  if (lowerMessage.includes('stripe') || lowerMessage.includes('payment')) {
    response = lang === 'es' 
      ? "Bluetooth$ utiliza Stripe para procesamiento de pagos seguro. Nuestro sistema de verificación de tarjetas con IA garantiza transacciones más seguras."
      : lang === 'fr'
      ? "Bluetooth$ utilise Stripe pour un traitement sécurisé des paiements. Notre système de vérification des cartes par IA garantit des transactions plus sûres."
      : "Bluetooth$ uses Stripe for secure payment processing. Our AI card verification system ensures safer transactions.";
  }
  
  if (lowerMessage.includes('test card') || lowerMessage.includes('test payment')) {
    response = lang === 'es' 
      ? "Puedes usar estas tarjetas de prueba para pruebas: 4242 4242 4242 4242, 4000 0566 5566 5556, o 5555 5555 5555 4444. Todas funcionan con cualquier fecha futura y CVV."
      : lang === 'fr'
      ? "Vous pouvez utiliser ces cartes de test pour les tests: 4242 4242 4242 4242, 4000 0566 5566 5556, ou 5555 5555 5555 4444. Elles fonctionnent toutes avec n'importe quelle date future et CVV."
      : "You can use these test cards for testing: 4242 4242 4242 4242, 4000 0566 5566 5556, or 5555 5555 5555 4444. They all work with any future date and CVV.";
  }
  
  // Try to find a contextual response based on current path
  if (!response) {
    response = findContextualResponse(currentPath, knowledge);
  }
  
  // If no contextual response, find response based on keywords
  if (!response) {
    response = findKeywordMatch(message, knowledge);
  }
  
  // If still no response, use a unique fallback that wasn't used recently
  if (!response) {
    response = getUniqueResponse(knowledge, recentResponses);
  }
  
  // Add this response to recent responses to avoid repetition
  addToRecentResponses(response);
  
  // Return the final response
  return response;
};
